import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  ChevronDown,
  Info,
  Loader2,
  MonitorPlay,
  Wrench,
} from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "@/lib/toast";
import { Button } from "@mcpjam/design-system/button";
import type { ServerWithName } from "@/state/app-types";
import type { ListToolsResultWithMetadata } from "@/lib/apis/mcp-tools-api";
import { evaluateAllHosts } from "@/lib/host-compat/engine";
import { useHostCatalog } from "@/lib/host-compat/use-host-catalog";
import { useWidgetUsageState } from "@/lib/host-compat/use-widget-usage";
import { ConformanceGate } from "@/components/compat/ConformanceGate";
import {
  COMPAT_DISPLAY_META,
  getCompatDisplayLabel,
  getCompatDisplayStatus,
} from "@/components/compat/verdict-meta";
import {
  LiveRenderRow,
  useLiveRenders,
} from "@/components/compat/LiveRenderRow";
import type {
  CompatFinding,
  CompatLane,
  HostCompatReport,
} from "@/lib/host-compat/types";
import { track } from "@/lib/analytics";
import { routePaths } from "@/lib/app-navigation";
import { useHostMutations } from "@/hooks/useClients";
import { getCatalogHost, getCatalogTemplate } from "@mcpjam/sdk/host-compat";
import { usePreviewedHostId } from "@/hooks/use-previewed-client-id";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { cloneHostTemplateInput } from "@/lib/client-config-v2";
import { useClaudeCodeHostEnabled } from "@/hooks/useClaudeCodeHostEnabled";
import { useCodexHostEnabled } from "@/hooks/useCodexHostEnabled";
import { filterReportsByFeatureFlags } from "@/lib/host-compat/feature-visibility";
import type { ToolsDataStatus } from "@/lib/host-compat/use-host-compat";

const FINDING_ICON: Record<
  CompatFinding["severity"],
  { Icon: typeof Info; className: string }
> = {
  blocker: { Icon: AlertCircle, className: "text-red-500" },
  degraded: { Icon: AlertTriangle, className: "text-amber-500" },
  info: { Icon: Info, className: "text-muted-foreground" },
};

/** Findings split into two axes — see `CompatLane`. Apps first (where hosts
 * most visibly differ), then Server (capability negotiation). */
const LANE_ORDER = ["apps", "server"] as const;
const LANE_LABEL: Record<CompatLane, string> = {
  apps: "Apps",
  server: "Server",
};

/**
 * Per-host compatibility report for the server detail modal's
 * Compatibility tab. Prototype of the L0 static report in
 * `design-explorations/host-compat-report.md`.
 */
export function HostCompatContent({
  server,
  toolsData,
  toolsLoadStatus,
  projectId,
  serverId,
  onClose,
  source = "compat_detail_modal",
}: {
  server: ServerWithName;
  toolsData?: ListToolsResultWithMetadata | null;
  toolsLoadStatus?: ToolsDataStatus;
  /**
   * Analytics surface this report is rendered on — keeps the host-creation
   * funnel honest (the standalone page must not tag its views/CTAs as modal).
   * Defaults to the modal so existing callers are unchanged.
   */
  source?: "compat_detail_modal" | "compat_page";
  /** Convex project id — required to create a host. */
  projectId?: string | null;
  /** Project-server-ref id to attach to the new host (the modal resolves it
   * from `hostedServerId`). Without it we can't attach this server, so the
   * CTA hides rather than create an empty host. */
  serverId?: string | null;
  /** Close the detail modal before we navigate to the playground. */
  onClose?: () => void;
}) {
  const widgetScan = useWidgetUsageState(server.name, toolsData);
  const widgetUsage = widgetScan.usage;
  const resolvedToolsLoadStatus =
    toolsLoadStatus ??
    (toolsData
      ? "ready"
      : server.connectionStatus === "connected"
      ? "loading"
      : "idle");
  const analysisStatus =
    server.connectionStatus === "connected" &&
    (resolvedToolsLoadStatus === "loading" ||
      widgetScan.status === "idle" ||
      widgetScan.status === "loading")
      ? "analyzing"
      : server.connectionStatus === "connected" &&
        (resolvedToolsLoadStatus === "failed" || widgetScan.status === "failed")
      ? "failed"
      : "ready";
  const analysisReady = analysisStatus === "ready";
  const protocolVersion = server.initializationInfo?.protocolVersion;
  // Live catalog in the deps: verdicts render from the bundled catalog first,
  // then recompute when the live fetch lands.
  const catalogState = useHostCatalog();
  const { requirements, reports } = useMemo(
    () =>
      evaluateAllHosts(
        toolsData,
        widgetUsage,
        { protocolVersion },
        catalogState?.catalog
      ),
    [toolsData, widgetUsage, protocolVersion, catalogState]
  );
  const claudeCodeEnabled = useClaudeCodeHostEnabled();
  const codexEnabled = useCodexHostEnabled();
  const visibleReports = useMemo(
    () =>
      filterReportsByFeatureFlags(reports, {
        claudeCode: claudeCodeEnabled,
        codex: codexEnabled,
      }),
    [reports, claudeCodeEnabled, codexEnabled]
  );

  // Tier-2: render the server's widget live in each host's emulation.
  const live = useLiveRenders(server.name, requirements);

  const navigate = useNavigate();
  const { createHost } = useHostMutations();
  const [, setPreviewedHostId] = usePreviewedHostId(projectId ?? null);
  const themeMode = usePreferencesStore((s) => s.themeMode);
  // Which host's CTA is mid-create (drives its spinner + disables the rest).
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(
    null
  );
  // Findings are collapsed by default — the row shows a terse summary; the
  // full list expands on demand so the tab reads as a scannable list.
  const [expandedHostId, setExpandedHostId] = useState<string | null>(null);
  const toggleExpanded = (hostId: string) =>
    setExpandedHostId((current) => (current === hostId ? null : hostId));

  // Top of the host-creation funnel: one "tab viewed" per server so the
  // compat → create conversion is measurable. Re-arms on server switch.
  const viewedServerRef = useRef<string | null>(null);
  useEffect(() => {
    if (viewedServerRef.current === server.name) return;
    viewedServerRef.current = server.name;
    track("host_compat_tab_viewed", {
      location: source,
      server_name: server.name,
      host_count: visibleReports.length,
    });
    // Intentionally keyed on server.name only — reports churn as tools load,
    // but this is a once-per-server view signal, not a verdict snapshot.
  }, [server.name, source, visibleReports.length]);

  // The CTA that turns a verdict into a host: create a host from the
  // matching template with THIS server attached, select it, and jump to the
  // playground. This is the insight → creation bridge the design doc calls
  // for ("Open in emulated {host}").
  const canCreateHosts = Boolean(projectId && serverId);
  const handleTestInHost = async (report: HostCompatReport) => {
    const templateId = report.hostId;
    if (!projectId || !serverId) return;
    const catalog =
      catalogState.status === "live" ? catalogState.catalog : null;
    const template = catalog
      ? getCatalogTemplate(catalog, templateId)
      : undefined;
    if (!template) {
      toast.error("Could not load live client templates");
      return;
    }
    const label =
      (catalog ? getCatalogHost(catalog, templateId)?.label : undefined) ??
      report.hostLabel;

    track("compat_cta_clicked", {
      location: source,
      template_id: templateId,
      host_label: report.hostLabel,
      verdict: report.verdict,
      server_name: server.name,
    });

    setCreatingTemplateId(templateId);
    try {
      const seed = cloneHostTemplateInput(template, { themeMode });
      const { hostId, hostConfigId } = await createHost({
        projectId,
        name: label,
        input: { ...seed, serverIds: [serverId] },
      });
      // Same event the create dialog fires, so host creation stays one
      // unified number — filter on `via`/`location` to isolate CTA-driven
      // creates. Best-effort: a posthog throw must not surface a failure
      // toast after the host already exists.
      try {
        track("client_created", {
          location: "compat_cta",
          via: "compat_report",
          template_id: templateId,
          client_id: hostId,
          client_config_id: hostConfigId,
          server_count: 1,
        });
      } catch {
        // swallow — analytics must not block the success path
      }
      setPreviewedHostId(hostId);
      onClose?.();
      navigate(routePaths.playground);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : `Couldn't open in ${label}`
      );
    } finally {
      setCreatingTemplateId(null);
    }
  };

  return (
    <div className="pb-4">
      <ConformanceGate server={server} />

      {analysisStatus === "analyzing" ? (
        <div className="mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking server compatibility…
        </div>
      ) : analysisStatus === "failed" ? (
        <p className="mb-2 text-[11px] text-muted-foreground">
          Couldn’t complete compatibility checks. Host status is unavailable.
        </p>
      ) : (
        <p className="pb-1 text-[11px] text-muted-foreground">
          Compatibility checks based on this server’s tools and widgets.
        </p>
      )}

      <div className="divide-y divide-border/50">
        {visibleReports.map((report) => {
          const displayStatus = analysisReady
            ? getCompatDisplayStatus(report)
            : null;
          const displayMeta = displayStatus
            ? {
                ...COMPAT_DISPLAY_META[displayStatus],
                label: getCompatDisplayLabel(report) ?? "",
              }
            : null;
          const hasFindings = analysisReady && report.findings.length > 0;
          const isOpen = expandedHostId === report.hostId;
          const summary = hasFindings
            ? `${report.findings[0].title}${
                report.findings.length > 1
                  ? ` +${report.findings.length - 1}`
                  : ""
              }`
            : "";
          const canCreateFromLiveTemplate =
            catalogState.status === "live" &&
            getCatalogTemplate(catalogState.catalog, report.hostId) !==
              undefined;
          return (
            <div key={report.hostId} className="py-2.5 first:pt-1.5">
              <div className="flex items-center gap-2">
                <img
                  src={report.logoSrcByTheme?.[themeMode] ?? report.logoSrc}
                  alt=""
                  className="h-4 w-4 flex-shrink-0 rounded-[3px] object-contain"
                />
                <span className="text-sm font-medium text-foreground">
                  {report.hostLabel}
                </span>
                {displayMeta ? (
                  <span
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 text-xs ${displayMeta.text}`}
                    aria-label={displayMeta.label}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${displayMeta.dot}`}
                    />
                    {displayMeta.label}
                  </span>
                ) : null}

                {hasFindings ? (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(report.hostId)}
                    aria-expanded={isOpen}
                    className="flex min-w-0 flex-1 items-center gap-1 rounded-md text-left text-xs text-muted-foreground hover:text-foreground"
                  >
                    <span className="truncate">{summary}</span>
                    <ChevronDown
                      className={`h-4 w-4 flex-shrink-0 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : (
                  <div className="min-w-0 flex-1" aria-hidden />
                )}

                <div className="flex flex-shrink-0 items-center">
                  {live.available &&
                    report.rendersWidgets &&
                    live.widgetTool && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                        disabled={live.runningHostId !== null}
                        onClick={() => live.run(report)}
                      >
                        {live.runningHostId === report.hostId ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Rendering…
                          </>
                        ) : (
                          <>
                            <MonitorPlay className="h-3 w-3" />
                            Run live
                          </>
                        )}
                      </Button>
                    )}
                  {canCreateHosts && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                      disabled={
                        creatingTemplateId !== null ||
                        !canCreateFromLiveTemplate
                      }
                      onClick={() => handleTestInHost(report)}
                      title={
                        canCreateFromLiveTemplate
                          ? "Test in client"
                          : "Live client template unavailable"
                      }
                    >
                      {creatingTemplateId === report.hostId ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Opening…
                        </>
                      ) : (
                        <>
                          Test
                          <ArrowUpRight className="h-3 w-3" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {live.results[report.hostId] && (
                <LiveRenderRow outcome={live.results[report.hostId]} />
              )}

              {hasFindings && isOpen && (
                <div className="mt-2 space-y-2.5 pl-6">
                  {LANE_ORDER.map((lane) => {
                    const laneFindings = report.findings.filter(
                      (f) => f.lane === lane
                    );
                    if (laneFindings.length === 0) return null;
                    const laneStatus = getCompatDisplayStatus({
                      verdict: report.lanes[lane].verdict,
                      findings: laneFindings,
                    });
                    const laneLabel = laneStatus
                      ? getCompatDisplayLabel({
                          verdict: report.lanes[lane].verdict,
                          findings: laneFindings,
                        })
                      : null;
                    return (
                      <div key={lane}>
                        <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          {laneStatus ? (
                            <span
                              className={`h-1 w-1 rounded-full ${COMPAT_DISPLAY_META[laneStatus].dot}`}
                            />
                          ) : null}
                          {LANE_LABEL[lane]}
                          {laneLabel ? ` · ${laneLabel}` : ""}
                        </div>
                        <ul className="space-y-1.5">
                          {laneFindings.map((finding, index) => {
                            const icon = FINDING_ICON[finding.severity];
                            return (
                              <li key={index} className="flex gap-2 text-xs">
                                <icon.Icon
                                  className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${icon.className}`}
                                />
                                <div className="min-w-0">
                                  <span className="font-medium text-foreground">
                                    {finding.title}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {": "}
                                    {finding.detail}
                                  </span>
                                  {finding.remediation && (
                                    <div className="mt-1 flex items-start gap-1.5 text-muted-foreground">
                                      <Wrench className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                      <span>{finding.remediation}</span>
                                    </div>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
